import { useState } from 'react';
import { Link } from 'react-router-dom';
import { deliveryBadge, type WebhookDelivery } from '@/lib/feexpayWebhook';

interface DeliveriesCardProps {
  deliveries: WebhookDelivery[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return value;
  }
}

function prettyJson(raw: string | null): string {
  if (!raw) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export default function DeliveriesCard({ deliveries, loading, error, onRefresh }: DeliveriesCardProps) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const received = deliveries.filter((d) => d.status === 'received').length;
  const toCheck = deliveries.filter((d) => d.status === 'rejected' || d.status === 'unmatched').length;

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50">
      <div className="flex items-center justify-between gap-3 p-5 pb-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-foreground-950">Dernières livraisons FeexPay</h3>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            Webhooks reçus et envois sortants — {deliveries.length} entrée(s), {received} traitée(s), {toCheck} à vérifier.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/superadmin/logs?tab=payments"
            className="h-8 px-3 rounded-full border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap flex items-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-book-open-line"></i>
            Journal complet
          </Link>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="h-8 px-3 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap flex items-center gap-1.5 hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
            Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-5 mb-4 rounded-md border border-red-200 bg-red-50/70 px-3 py-2 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-600 mt-0.5"></i>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-red-700">Impossible de charger les livraisons</p>
            <p className="text-[11px] text-foreground-600 mt-0.5 break-words">{error}</p>
          </div>
        </div>
      )}

      {loading && deliveries.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-2">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <span className="text-[11px] text-foreground-400">Chargement…</span>
        </div>
      ) : deliveries.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-2 px-5 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-inbox-line text-xl text-foreground-300"></i>
          </div>
          <p className="text-xs font-medium text-foreground-600">Aucune livraison enregistrée</p>
          <p className="text-[11px] text-foreground-400 max-w-md">
            Dès qu’un webhook arrive ou qu’un paiement est envoyé, la ligne apparaît ici avec la réponse brute de FeexPay.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-background-200/70 border-t border-background-200/70">
          {deliveries.map((delivery) => {
            const badge = deliveryBadge(delivery.status);
            const isOpen = expanded === delivery.id;
            return (
              <div key={delivery.id}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : delivery.id)}
                  className="w-full flex items-center gap-3 px-4 md:px-5 py-2.5 text-left hover:bg-background-100/70 transition-colors cursor-pointer"
                >
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap flex items-center gap-1 ${badge.className}`}>
                    <i className={badge.icon}></i>
                    {badge.label}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11px] font-medium text-foreground-800 truncate">
                      {delivery.reference || 'Sans référence'}
                    </span>
                    <span className="block text-[10px] text-foreground-400 truncate">
                      {delivery.error_message || '—'}
                    </span>
                  </span>
                  <span className="hidden sm:flex flex-col items-end flex-shrink-0">
                    <span className="text-[11px] font-semibold text-foreground-800">
                      {delivery.amount != null ? `${delivery.amount.toLocaleString('fr-FR')} ${delivery.currency || ''}` : '—'}
                    </span>
                    <span className="text-[10px] text-foreground-400">{formatDate(delivery.created_at)}</span>
                  </span>
                  <i className={`flex-shrink-0 text-foreground-400 ${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>

                <div className="sm:hidden px-4 pb-2 -mt-1 flex items-center gap-3 text-[10px] text-foreground-400">
                  <span>{delivery.amount != null ? `${delivery.amount.toLocaleString('fr-FR')} ${delivery.currency || ''}` : '—'}</span>
                  <span>{formatDate(delivery.created_at)}</span>
                </div>

                {isOpen && (
                  <div className="px-4 md:px-5 pb-4 bg-background-100/50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      <div className="rounded-md border border-background-200/70 bg-background-50 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-foreground-400">Endpoint</p>
                        <p className="text-[11px] font-mono text-foreground-800 truncate">{delivery.endpoint || '—'}</p>
                      </div>
                      <div className="rounded-md border border-background-200/70 bg-background-50 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-foreground-400">Parcours</p>
                        <p className="text-[11px] text-foreground-800 truncate">{delivery.flow || '—'}</p>
                      </div>
                      <div className="rounded-md border border-background-200/70 bg-background-50 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-foreground-400">Opérateur</p>
                        <p className="text-[11px] text-foreground-800 truncate">{delivery.operator || '—'}</p>
                      </div>
                      <div className="rounded-md border border-background-200/70 bg-background-50 px-2.5 py-1.5">
                        <p className="text-[9px] uppercase tracking-wide text-foreground-400">HTTP</p>
                        <p className="text-[11px] text-foreground-800">{delivery.http_status ?? '—'}</p>
                      </div>
                    </div>
                    <p className="text-[10px] uppercase tracking-wide text-foreground-400 mb-1">Réponse brute FeexPay</p>
                    <pre className="max-h-64 overflow-auto rounded-md border border-background-200/70 bg-foreground-950 text-background-50 text-[10px] leading-relaxed p-3 font-mono">
{prettyJson(delivery.raw_response)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}